import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, RefreshCw, Trash2, Loader2, Calendar as CalendarIcon,
  CheckCircle, AlertCircle, Link as LinkIcon, Smartphone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = ['#A2D2FF', '#FFC8DD', '#B9FBC0', '#FFE8A3', '#CDB4DB', '#FFB4A2', '#FF6B9D', '#7DD3C0'];

function fmtRelative(iso) {
  if (!iso) return 'aldrig';
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return 'just nu';
  if (diff < 3600) return `${Math.round(diff / 60)} min sedan`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h sedan`;
  return t.toLocaleDateString('sv-SE');
}

// ============== ADD FEED MODAL ==============
function AddFeedModal({ open, onClose, onAdded }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (open) { setUrl(''); setName(''); setColor(PALETTE[0]); setShowHelp(false); }
  }, [open]);

  if (!open) return null;

  const save = async () => {
    const u = url.trim();
    if (!u) { toast.error('Klistra in en kalender-länk'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/calendar-sync/feeds`, {
        url: u, name: name.trim() || null, color,
      }, { withCredentials: true });
      toast.success('Kalender ansluten! Hämtar händelser… 📅');
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte ansluta');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 overflow-y-auto"
         onClick={onClose} data-testid="addfeed-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="neo-card bg-white p-4 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#D06892]" /> Anslut Apple-kalender
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <button
          onClick={() => setShowHelp((v) => !v)}
          className="w-full neo-btn p-2 bg-[#FFE8F5] text-xs font-bold flex items-center gap-1"
          data-testid="addfeed-help-toggle"
        >
          <Smartphone className="w-3 h-3" /> Visa instruktioner (iOS · Google · Outlook)
        </button>

        {showHelp && (
          <div className="neo-card p-3 bg-[#FFFAF0] text-xs space-y-3" data-testid="addfeed-help">
            <div>
              <p className="font-bold flex items-center gap-1 mb-1">
                🍎 iOS-Kalender (iCloud)
              </p>
              <ol className="space-y-0.5 list-decimal ml-4 opacity-90">
                <li>Öppna <b>Kalender</b>-appen på iPhone</li>
                <li>Tryck på <b>Kalendrar</b> längst ner</li>
                <li>Tryck på <b>(i)</b> bredvid kalendern</li>
                <li>Slå på <b>Offentlig kalender</b> → <b>Dela länk</b></li>
                <li>Kopiera länken (<code>webcal://...</code>) och klistra in nedan 👇</li>
              </ol>
            </div>

            <div>
              <p className="font-bold flex items-center gap-1 mb-1">
                📨 Outlook / Office 365
              </p>
              <ol className="space-y-0.5 list-decimal ml-4 opacity-90">
                <li>Öppna Outlook på webben → <b>Inställningar</b> → <b>Kalender</b></li>
                <li>Klicka <b>Delade kalendrar</b> → <b>Publicera en kalender</b></li>
                <li>Välj din kalender, behörighet <b>"Kan se all information"</b></li>
                <li>Klicka <b>Publicera</b> → kopiera <b>ICS-länken</b> (slutar på <code>.ics</code>)</li>
                <li>Klistra in länken nedan 👇</li>
              </ol>
              <p className="text-[10px] opacity-60 italic mt-0.5">
                💡 På Outlook-skrivbordet: Fil → Kontoinställningar → Internetkalendrar
              </p>
            </div>

            <div>
              <p className="font-bold flex items-center gap-1 mb-1">
                📆 Google Calendar
              </p>
              <ol className="space-y-0.5 list-decimal ml-4 opacity-90">
                <li>Öppna Google Calendar (på datorn) → klicka på kugghjulet → <b>Inställningar</b></li>
                <li>Välj din kalender i vänstermenyn</li>
                <li>Scrolla ned till <b>"Hemlig adress i iCal-format"</b></li>
                <li>Kopiera länken (slutar på <code>basic.ics</code>) och klistra in nedan 👇</li>
              </ol>
              <p className="text-[10px] opacity-60 italic mt-0.5">
                💡 För offentliga kalendrar funkar även "Offentlig adress i iCal-format"
              </p>
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1 flex items-center gap-1">
            <LinkIcon className="w-3 h-3" /> Kalender-länk
          </p>
          <input
            type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="webcal://p01-calendars.icloud.com/published/..."
            className="neo-input w-full text-xs"
            data-testid="addfeed-url"
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
          />
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Namn (valfritt)</p>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="t.ex. Jobb, Privat, Familj"
            className="neo-input w-full text-sm"
            data-testid="addfeed-name"
          />
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Färg</p>
          <div className="flex gap-1 flex-wrap">
            {PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full border-2 transition ${color === c ? 'border-black ring-2 ring-[#D06892]' : 'border-black/10'}`}
                style={{ background: c }}
                data-testid={`addfeed-color-${c}`}
              />
            ))}
          </div>
        </div>

        <button onClick={save} disabled={busy || !url.trim()}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base"
          data-testid="addfeed-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Anslut kalender 💖'}
        </button>
      </div>
    </div>
  );
}

// ============== FEED CARD ==============
function FeedCard({ feed, onSync, onDelete, syncing }) {
  const status = feed.last_sync_status;
  const synced = fmtRelative(feed.last_synced_at);
  return (
    <div className="neo-card p-3 bg-white" style={{ borderLeft: `6px solid ${feed.color || '#A2D2FF'}` }}
         data-testid={`feed-${feed.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-['Fredoka'] font-bold text-base truncate flex items-center gap-1">
            {feed.name}
          </h4>
          <p className="text-[10px] opacity-50 truncate font-mono">{feed.url}</p>
          <div className="flex items-center gap-2 mt-1 text-[11px]">
            <span className="flex items-center gap-1 opacity-70">
              {status === 'ok' ? <CheckCircle className="w-3 h-3 text-green-600" /> :
               status === 'error' ? <AlertCircle className="w-3 h-3 text-red-500" /> :
               <Loader2 className="w-3 h-3" />}
              {status === 'ok' ? `${feed.event_count} händelser` :
               status === 'error' ? 'Fel — försök igen' :
               'Inte synkad än'}
            </span>
            <span className="opacity-50">·</span>
            <span className="opacity-60">{synced}</span>
          </div>
          {feed.last_sync_error && (
            <p className="text-[10px] text-red-500 mt-1 italic">{feed.last_sync_error}</p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onSync(feed.id)} disabled={syncing}
            className="neo-btn p-1.5 bg-white text-xs"
            data-testid={`feed-sync-${feed.id}`}
            title="Synka nu"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => onDelete(feed.id)}
            className="neo-btn p-1.5 bg-white text-xs"
            data-testid={`feed-delete-${feed.id}`}
            title="Ta bort"
          >
            <Trash2 className="w-3 h-3 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== MAIN ==============
export default function AppleCalendarSync() {
  const [feeds, setFeeds] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [syncAllBusy, setSyncAllBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [fr, er] = await Promise.all([
        axios.get(`${API}/calendar-sync/feeds`, { withCredentials: true }),
        axios.get(`${API}/calendar-sync/events`, {
          withCredentials: true,
          params: {
            start: new Date(Date.now() - 7 * 86400_000).toISOString(),
            end: new Date(Date.now() + 60 * 86400_000).toISOString(),
          },
        }),
      ]);
      setFeeds(fr.data?.feeds || []);
      setEvents(er.data?.events || []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncFeed = async (id) => {
    setSyncing(id);
    try {
      const { data } = await axios.post(`${API}/calendar-sync/feeds/${id}/sync`, {}, { withCredentials: true });
      if (data?.sync?.ok) {
        toast.success(`Synkade ${data.sync.count} händelser`);
      } else {
        toast.error(data?.sync?.error || 'Synkning misslyckades');
      }
      load();
    } catch {
      toast.error('Synkning misslyckades');
    } finally { setSyncing(null); }
  };

  const syncAll = async () => {
    if (feeds.length === 0) return;
    setSyncAllBusy(true);
    try {
      const { data } = await axios.post(`${API}/calendar-sync/sync-all`, {}, { withCredentials: true });
      toast.success(`Synkade ${data.synced_feeds} kalendrar — ${data.total_events} händelser totalt 🎉`);
      load();
    } catch {
      toast.error('Kunde inte synka alla kalendrar');
    } finally { setSyncAllBusy(false); }
  };

  const removeFeed = async (id) => {
    if (!window.confirm('Ta bort kalendern och alla synkade händelser?')) return;
    try {
      await axios.delete(`${API}/calendar-sync/feeds/${id}`, { withCredentials: true });
      toast.success('Kalender borttagen');
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  // Total event count across all feeds
  const totalEventCount = feeds.reduce((sum, f) => sum + (f.event_count || 0), 0);

  // Group events by date (only upcoming 14 days for display)
  const upcoming = events
    .filter((e) => new Date(e.end) >= new Date(Date.now() - 86400_000))
    .slice(0, 20);

  return (
    <div className="space-y-3" data-testid="apple-cal-sync">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#E8F4FF] to-[#FFFAF0]">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">
              Apple Calendar Sync
            </p>
            <h2 className="font-['Fredoka'] font-bold text-xl mt-0.5">
              📅 Mina externa kalendrar
            </h2>
            <p className="text-xs opacity-70 mt-1">
              Anslut iCloud, Google Calendar eller Outlook/Office 365 — vi synkar automatiskt var 15:e minut 💖
            </p>
          </div>
          <div className="flex gap-1 flex-wrap">
            {feeds.length > 0 && (
              <button onClick={syncAll} disabled={syncAllBusy}
                className="neo-btn px-3 py-2 bg-[#A2D2FF] text-sm font-bold flex items-center gap-1"
                data-testid="sync-all-btn"
                title="Tvinga omedelbar synk av alla kalendrar"
              >
                {syncAllBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Synka alla
              </button>
            )}
            <button onClick={() => setShowAdd(true)}
              className="neo-btn px-3 py-2 bg-[#FFC8DD] text-sm font-bold flex items-center gap-1"
              data-testid="addfeed-open"
            >
              <Plus className="w-4 h-4" /> Lägg till
            </button>
          </div>
        </div>

        {/* Stats summary row */}
        {feeds.length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap" data-testid="apple-cal-stats">
            <div className="neo-card px-3 py-1.5 bg-white text-xs flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 text-[#D06892]" />
              <span className="font-bold">{feeds.length}</span>
              <span className="opacity-60">{feeds.length === 1 ? 'kalender' : 'kalendrar'}</span>
            </div>
            <div className="neo-card px-3 py-1.5 bg-white text-xs flex items-center gap-1.5">
              <span>🍎</span>
              <span className="font-bold" data-testid="total-event-count">{totalEventCount}</span>
              <span className="opacity-60">{totalEventCount === 1 ? 'händelse synkad' : 'händelser synkade'}</span>
            </div>
            <div className="neo-card px-3 py-1.5 bg-white text-xs flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              <span className="opacity-60">Auto-synk var 15:e min</span>
            </div>
          </div>
        )}
      </header>

      {/* Feeds */}
      {busy ? (
        <div className="neo-card p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
        </div>
      ) : feeds.length === 0 ? (
        <div className="neo-card p-8 text-center" data-testid="apple-cal-empty">
          <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-[#D06892]" />
          <p className="text-base font-bold">Ingen kalender ansluten än</p>
          <p className="text-xs opacity-60 mt-1">
            Koppla in din iOS-Kalender så syns alla händelser här automatiskt
          </p>
          <button onClick={() => setShowAdd(true)}
            className="neo-btn mt-3 px-4 py-2 bg-[#FFC8DD] font-bold text-sm"
            data-testid="apple-cal-empty-cta"
          >
            + Anslut min första kalender
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="feeds-list">
          {feeds.map((f) => (
            <FeedCard key={f.id} feed={f} onSync={syncFeed} onDelete={removeFeed} syncing={syncing === f.id} />
          ))}
        </div>
      )}

      {/* Upcoming events preview */}
      {upcoming.length > 0 && (
        <section className="neo-card p-3 bg-white" data-testid="upcoming-events">
          <h3 className="font-['Fredoka'] font-bold text-sm mb-2 flex items-center gap-1">
            ✨ Kommande händelser ({upcoming.length})
          </h3>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {upcoming.map((e) => {
              const start = new Date(e.start);
              const dateStr = start.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
              const timeStr = e.all_day ? 'hela dagen' : start.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={e.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[#FFF0F8] text-xs"
                     style={{ borderLeft: `3px solid ${e.feed_color || '#A2D2FF'}` }}
                     data-testid={`event-${e.id}`}>
                  <div className="w-16 shrink-0 text-[10px] font-bold opacity-70">{dateStr}</div>
                  <div className="w-14 shrink-0 text-[10px] opacity-60">{timeStr}</div>
                  <div className="flex-1 min-w-0 truncate font-medium">{e.title}</div>
                  <div className="text-[9px] opacity-50 shrink-0 truncate max-w-[80px]">{e.feed_name}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <AddFeedModal open={showAdd} onClose={() => setShowAdd(false)} onAdded={load} />
    </div>
  );
}
